import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { useTheme } from '@mui/material/styles';
import { getDeviceStatusColorMaps } from '../../theme/uiSemanticColors';
import {
  Refresh as RefreshIcon,
  Security as GuardIcon,
  CheckCircle as HealthyIcon,
  Warning as DegradedIcon,
  Error as UnhealthyIcon,
  Sync as SyncIcon,
  SyncProblem as SyncProblemIcon,
} from '@mui/icons-material';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { opsGuardDashboardService } from '../../services/OpsGuardDashboardService';
import type {
  GuardDashboardRow,
  GuardStatus,
} from '@cnapse/shared/schemas.js';
import { getDeviceCategoryLabel } from '@cnapse/shared';
import { EnhancedTable, type TableColumn, type FilterConfig } from '../../components/ui/EnhancedTable';

const GUARD_STATUS_COLORS: Record<GuardStatus, 'success' | 'warning' | 'error' | 'default'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
  unknown: 'default',
};

const CACHE_STALE_SECONDS = 3 * 60 * 60; // 3 hours
const HEARTBEAT_STALE_MS = 5 * 60 * 1000; // 5 minutes — matches backend guard-registry.service

function isHeartbeatStale(lastSeenAt: string | null | undefined): boolean {
  if (!lastSeenAt) return true;
  return Date.now() - new Date(lastSeenAt).getTime() > HEARTBEAT_STALE_MS;
}

function SummaryCard({ label, value, color, icon }: { label: string; value: number; color: string; icon: React.ReactNode }) {
  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ color }}>{value.toLocaleString()}</Typography>
      </CardContent>
    </Card>
  );
}

function formatCacheAge(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatGatewayType(deviceCategory: string | null | undefined): string {
  if (!deviceCategory) return '—';
  return getDeviceCategoryLabel(deviceCategory);
}

export default function GuardDashboard() {
  const theme = useTheme();
  const statusMaps = useMemo(() => getDeviceStatusColorMaps(theme), [theme]);
  const [rows, setRows] = useState<GuardDashboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilters, setTableFilters] = useState<Record<string, string>>({
    tenantId: '',
    siteId: '',
    deviceCategory: '',
    guardStatus: '',
    registryNeedsUpdate: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let cursor: string | undefined;
      const allRows: GuardDashboardRow[] = [];

      do {
        const result = await opsGuardDashboardService.getGuardDashboardList({
          cursor,
          limit: 200,
        });
        allRows.push(...result.data.guards);
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);

      const tenantsData = await providerTenantsService.getTenants();

      setRows(allRows);
      setTenants(tenantsData as Tenant[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load guard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, tableFilters, pageSize]);

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  const tenantNameById = useMemo(() => {
    return new Map<string, string>(tenants.map((tenant) => [String(tenant.id), tenant.name]));
  }, [tenants]);

  const filterOptions = useMemo((): FilterConfig[] => {
    const siteOptions = Array.from(new Set(rows.map((row) => row.siteId).filter(Boolean) as string[]))
      .sort((left, right) => left.localeCompare(right))
      .map((siteId) => ({ label: siteId, value: siteId }));

    return [
      {
        field: 'tenantId',
        label: 'Tenant',
        type: 'select',
        options: tenants.map((tenant) => ({ label: tenant.name, value: tenant.id })),
      },
      {
        field: 'siteId',
        label: 'Site',
        type: 'select',
        options: siteOptions,
      },
      {
        field: 'deviceCategory',
        label: 'Gateway type',
        type: 'select',
        options: [
          { label: getDeviceCategoryLabel('efem-gateway'), value: 'efem-gateway' },
          { label: getDeviceCategoryLabel('location-gateway'), value: 'location-gateway' },
        ],
      },
      {
        field: 'guardStatus',
        label: 'Guard Status',
        type: 'select',
        options: [
          { label: 'Healthy', value: 'healthy' },
          { label: 'Degraded', value: 'degraded' },
          { label: 'Unhealthy', value: 'unhealthy' },
          { label: 'Unknown', value: 'unknown' },
        ],
      },
      {
        field: 'registryNeedsUpdate',
        label: 'Needs Update',
        type: 'select',
        options: [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' },
          { label: 'Unknown', value: 'unknown' },
        ],
      },
    ];
  }, [rows, tenants]);

  const filteredRows = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      if (tableFilters.tenantId && row.tenantId !== tableFilters.tenantId) return false;
      if (tableFilters.siteId && row.siteId !== tableFilters.siteId) return false;
      if (tableFilters.deviceCategory && row.deviceCategory !== tableFilters.deviceCategory) return false;
      if (tableFilters.guardStatus && row.guardStatus !== tableFilters.guardStatus) return false;

      if (tableFilters.registryNeedsUpdate) {
        const filterValue = tableFilters.registryNeedsUpdate;
        if (filterValue === 'true' && row.registryNeedsUpdate !== true) return false;
        if (filterValue === 'false' && row.registryNeedsUpdate !== false) return false;
        if (filterValue === 'unknown' && row.registryNeedsUpdate != null) return false;
      }

      if (!searchValue) return true;

      const tenantName = row.tenantId ? tenantNameById.get(row.tenantId) ?? '' : '';
      const haystack = [
        row.gatewaySerialNumber,
        row.tenantId ?? '',
        tenantName,
        row.siteId ?? '',
        row.deviceCategory ?? '',
        row.guardStatus,
        row.guardServiceVersion ?? '',
        row.currentRegistryVersion ?? '',
        row.targetRegistryVersion ?? '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchValue);
    });
  }, [rows, searchTerm, tableFilters, tenantNameById]);

  const summary = useMemo(() => {
    return filteredRows.reduce(
      (accumulator, row) => {
        accumulator.guardsReporting += 1;
        accumulator[row.guardStatus] += 1;
        if (row.registryNeedsUpdate === true) accumulator.needsUpdate += 1;
        if ((row.cacheAgeSeconds ?? 0) > CACHE_STALE_SECONDS) accumulator.staleCache += 1;
        return accumulator;
      },
      { guardsReporting: 0, healthy: 0, degraded: 0, unhealthy: 0, needsUpdate: 0, staleCache: 0, unknown: 0 },
    );
  }, [filteredRows]);

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredRows.slice(startIndex, startIndex + pageSize);
  }, [filteredRows, page, pageSize]);

  const columns: TableColumn<GuardDashboardRow>[] = [
    {
      field: 'gatewaySerialNumber',
      headerName: 'Gateway Serial',
      renderCell: (row: GuardDashboardRow) => <Typography variant="body2" fontFamily="monospace">{row.gatewaySerialNumber}</Typography>,
    },
    {
      field: 'deviceCategory',
      headerName: 'Gateway type',
      renderCell: (row: GuardDashboardRow) => formatGatewayType(row.deviceCategory),
    },
    {
      field: 'tenantId',
      headerName: 'Tenant',
      renderCell: (row: GuardDashboardRow) => {
        const tenant = tenants.find((t) => t.id === row.tenantId);
        return <Typography variant="body2">{tenant?.name ?? row.tenantId ?? '—'}</Typography>;
      },
    },
    { field: 'siteId', headerName: 'Site', renderCell: (row: GuardDashboardRow) => row.siteId ?? '—' },
    {
      field: 'guardHeartbeatLastSeenAt',
      headerName: 'Guard Last Seen',
      renderCell: (row: GuardDashboardRow) =>
        row.guardHeartbeatLastSeenAt
          ? new Date(row.guardHeartbeatLastSeenAt).toLocaleString()
          : '—',
    },
    {
      field: 'guardStatus',
      headerName: 'Guard Status',
      renderCell: (row: GuardDashboardRow) => (
        <Chip label={row.guardStatus} color={GUARD_STATUS_COLORS[row.guardStatus]} size="small" />
      ),
    },
    {
      field: 'guardServiceVersion',
      headerName: 'Guard Version',
      renderCell: (row: GuardDashboardRow) => (
        <Typography variant="body2" fontFamily="monospace">{row.guardServiceVersion ?? '—'}</Typography>
      ),
    },
    {
      field: 'currentRegistryVersion',
      headerName: 'Current Registry',
      renderCell: (row: GuardDashboardRow) => (
        <Typography variant="body2" fontFamily="monospace">{row.currentRegistryVersion ?? 'unknown'}</Typography>
      ),
    },
    {
      field: 'targetRegistryVersion',
      headerName: 'Target Registry',
      renderCell: (row: GuardDashboardRow) => (
        <Typography variant="body2" fontFamily="monospace">{row.targetRegistryVersion ?? '—'}</Typography>
      ),
    },
    {
      field: 'lastRegistrySyncAt',
      headerName: 'Last Registry Sync',
      renderCell: (row: GuardDashboardRow) =>
        row.lastRegistrySyncAt ? new Date(row.lastRegistrySyncAt).toLocaleString() : '—',
    },
    {
      field: 'cacheAgeSeconds',
      headerName: 'Cache Age',
      renderCell: (row: GuardDashboardRow) => {
        if (isHeartbeatStale(row.guardHeartbeatLastSeenAt)) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const age = row.cacheAgeSeconds;
        const isStale = age != null && age > CACHE_STALE_SECONDS;
        return (
          <Typography
            variant="body2"
            sx={{ color: isStale ? 'warning.main' : 'inherit', fontWeight: isStale ? 600 : 400 }}
          >
            {formatCacheAge(age)}
          </Typography>
        );
      },
    },
    {
      field: 'registryNeedsUpdate',
      headerName: 'Needs Update',
      renderCell: (row: GuardDashboardRow) => {
        if (row.registryNeedsUpdate === null || row.registryNeedsUpdate === undefined) {
          return <Chip label="unknown" size="small" />;
        }
        return row.registryNeedsUpdate ? (
          <Chip label="yes" color="warning" size="small" icon={<SyncProblemIcon />} />
        ) : (
          <Chip label="no" color="success" size="small" icon={<SyncIcon />} />
        );
      },
    },
  ];

  return (
    <AppListPage>
      {/* Summary Cards */}
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))" gap={2} mb={3}>
        <SummaryCard label="Reporting" value={summary.guardsReporting} color={statusMaps.health.total} icon={<GuardIcon />} />
        <SummaryCard label="Healthy" value={summary.healthy} color={statusMaps.health.healthy} icon={<HealthyIcon />} />
        <SummaryCard label="Degraded" value={summary.degraded} color={statusMaps.health.degraded} icon={<DegradedIcon />} />
        <SummaryCard label="Unhealthy" value={summary.unhealthy} color={statusMaps.health.unhealthy} icon={<UnhealthyIcon />} />
        <SummaryCard label="Needs Update" value={summary.needsUpdate} color={statusMaps.health.needsUpdate} icon={<SyncProblemIcon />} />
        <SummaryCard label="Stale Cache" value={summary.staleCache} color={statusMaps.health.staleCache} icon={<SyncIcon />} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <EnhancedTable
        data={pagedRows}
        totalCount={filteredRows.length}
        columns={columns}
        actions={[]}
        getRowId={(row) => row.gatewaySerialNumber}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
        searchable={true}
        searchTerm={searchTerm}
        searchPlaceholder="Search gateway, tenant, site, registry..."
        onSearchChange={setSearchTerm}
        filters={tableFilters}
        filterOptions={filterOptions}
        onFilterChange={(filters) => setTableFilters(filters as Record<string, string>)}
        emptyStateTitle="No guard services found"
        emptyStateDescription="Use search or filters, or refresh to try again."
        maxHeight="calc(100vh - 360px)"
      />
    </AppListPage>
  );
}
